import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataLoader } from "../components/DataLoader";
import { useContestData } from "./contest/hooks/useContestData";
import { useContestFilters } from "./contest/hooks/useContestFilters";
import { useSortData } from "./hooks/useSortData";
import { usePagination } from "./hooks/usePagination";
import { ContestTable } from "./contest/ContestTable";
import { ContestFilters } from "./contest/ContestFilters";
import { ExportButtons } from "./contest/ExportButtons";
import { TablePagination } from "./members/TablePagination";
import { SearchBar } from "./members/SearchBar";
import { DateRangeFilter } from "./members/DateRangeFilter";

export const ContestTab = () => {
  const { registrations, isLoading, error } = useContestData();

  const {
    selectedAcademy,
    searchTerm,
    startDate,
    endDate,
    filteredRegistrations,
    handleAcademyChange,
    handleSearchChange,
    handleStartDateChange,
    handleEndDateChange,
  } = useContestFilters(registrations);

  const { sortedData, sortConfig, sortData } = useSortData(filteredRegistrations);

  const ITEMS_PER_PAGE = 20;
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    nextPage,
    prevPage,
  } = usePagination({
    data: sortedData,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  // Calculate totals
  const totalParticipants = sortedData.reduce((sum, reg) => sum + reg.participants_count, 0);
  const totalClasses = sortedData.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Inscriptions au concours "Exprime la Charte"</CardTitle>
          <CardDescription>
            {totalClasses} classe{totalClasses > 1 ? "s" : ""} inscrite{totalClasses > 1 ? "s" : ""} • {totalParticipants} participant{totalParticipants > 1 ? "s" : ""}
          </CardDescription>
        </div>
        <ExportButtons registrations={sortedData} />
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="w-full md:w-1/2">
              <SearchBar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder="Rechercher par établissement, enseignant, ville..."
              />
            </div>

            <ContestFilters
              selectedAcademy={selectedAcademy}
              onAcademyChange={handleAcademyChange}
            />
          </div>

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </div>

        <DataLoader
          isLoading={isLoading}
          error={error?.message || null}
          isEmpty={sortedData.length === 0}
          emptyMessage="Aucune inscription au concours trouvée"
        >
          <>
            <ContestTable
              registrations={paginatedData}
              sortConfig={sortConfig}
              onSort={sortData}
            />

            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-muted-foreground">
                Affichage de {paginatedData.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} à{" "}
                {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} sur {sortedData.length} inscriptions
              </div>

              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          </>
        </DataLoader>
      </CardContent>
    </Card>
  );
};
